import React from "react";
import { Card, Alert } from "react-bootstrap";

function ExecutingRunbooks() {
  return (
    <div>
      <h2 className="mb-4">Executing Runbooks</h2>

      <Alert variant="info">
        Learn how to run runbooks manually and view their execution results.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>Running a Runbook Manually</h4>
          <ol>
            <li>Go to <strong>My Runbooks</strong></li>
            <li>Click on a runbook to select it</li>
            <li>Click the <strong>Execute</strong> button</li>
            <li>Confirm that you want to run the runbook</li>
            <li>The runbook will begin executing immediately</li>
          </ol>
          <p className="mt-3">
            <em>Note: You need the "runbooks.execute" permission to run runbooks.</em>
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Viewing Execution History</h4>
          <ol>
            <li>Go to <strong>My Runbooks</strong></li>
            <li>Select a runbook</li>
            <li>Click the <strong>History</strong> button or icon</li>
            <li>You'll see a list of all past executions for this runbook</li>
          </ol>
          <p className="mt-3">The execution history shows:</p>
          <ul>
            <li>Start and end times</li>
            <li>Execution status (Completed, Error, etc.)</li>
            <li>How the execution was triggered (Manual or Scheduled)</li>
            <li>Who initiated the execution</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Viewing Detailed Execution Results</h4>
          <ol>
            <li>In the execution history, click on any execution row</li>
            <li>Click the <strong>View Lines</strong> button</li>
            <li>You'll see a detailed breakdown of each step that was executed</li>
          </ol>
          <p className="mt-3">For each step, you can see:</p>
          <ul>
            <li>Step number in the workflow</li>
            <li>Record number being processed</li>
            <li>Any error messages if the step failed</li>
            <li>Timestamp of when the step ran</li>
            <li>Output data from the step</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Understanding Execution Status</h4>
          <ul>
            <li><strong>Not Started</strong> - Execution is queued but hasn't begun</li>
            <li><strong>In Process</strong> - Currently running</li>
            <li><strong>Completed</strong> - Finished successfully with no errors</li>
            <li><strong>Error</strong> - Failed to complete due to an error</li>
            <li><strong>Authentication Failed</strong> - Could not connect to an external system</li>
            <li><strong>Completed with Errors</strong> - Finished but some steps had issues</li>
            <li><strong>Runbook Timeout</strong> - Took too long and was stopped</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Exporting Execution Data</h4>
          <p>
            You can export execution history to Excel for further analysis:
          </p>
          <ol>
            <li>View the execution history for a runbook</li>
            <li>Click the <strong>Export to Excel</strong> button</li>
            <li>An Excel file will be downloaded with all execution data</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Troubleshooting Failed Executions</h4>
          <p>If a runbook execution fails:</p>
          <ol>
            <li>Check the execution status for clues about what went wrong</li>
            <li>View the execution lines to see which specific step failed</li>
            <li>Look at the error message for the failed step</li>
            <li>Common issues include:
              <ul className="mt-2">
                <li>Invalid connector credentials</li>
                <li>Missing or incorrect variable data</li>
                <li>External system unavailable</li>
                <li>Invalid data format</li>
              </ul>
            </li>
            <li>Fix the issue and try executing again</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Email Notifications</h4>
          <p>
            If email notifications are configured for a runbook, you'll receive an email when the runbook execution completes,
            including the status and any relevant results or error messages.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
}

export default ExecutingRunbooks;
